/**
 * Appearance preference. "system" follows the OS; light/dark pin it. Only the
 * chrome changes — canvases and exports stay black ink on white paper.
 *
 * index.html applies the stored choice before first paint, so there is no flash.
 */

export type ThemePref = 'system' | 'light' | 'dark';

const KEY = 'panels:theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

export function readThemePref(): ThemePref {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Storage blocked: fall back to the system setting.
  }
  return 'system';
}

export function effectiveTheme(pref: ThemePref = readThemePref()): 'light' | 'dark' {
  if (pref !== 'system') return pref;
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

export function setThemePref(pref: ThemePref): void {
  try {
    if (pref === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, pref);
  } catch {
    // Still applies for this session.
  }
  applyTheme(pref);
}

export function applyTheme(pref: ThemePref = readThemePref()): void {
  const root = document.documentElement;
  if (pref === 'system') delete root.dataset.theme;
  else root.dataset.theme = pref;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', effectiveTheme(pref) === 'dark' ? '#16150f' : '#faf9f7');
}
